import React, { useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { BackupVmMapping } from '../../../types/models/BackupVmMapping';
import type { ApiResponseBackupVmMappingDeleted } from '../../../types/api/ApiResponseBackupVmMapping';

interface BackupAttributionRemoveModalProps {
  mapping: BackupVmMapping;
  onClose: () => void;
  /** Drop the removed row from the page's lists. `revertedTo` is the backend's own answer. */
  onRemoved: (removed: BackupVmMapping, revertedTo: number | null) => void;
}

/**
 * Destructive confirm for removing a per-VM attribution.
 *
 * DELETE does not "unset" the attribution — it **reverts** the subject to its directory mapping's
 * client in the same request. The consequence is therefore stated BEFORE the call, from the row's
 * own `directory_client_name`, because the two outcomes differ enormously:
 *
 *   - the directory has a client  → the VM's backups move to that client;
 *   - the directory is unattributed → the VM becomes invisible to EVERY customer.
 *
 * The reassurance that no backup data is destroyed is not decoration: it is the first thing a
 * staff member worries about, and it is true — the backend touches no runs and no disk chains.
 */
function BackupAttributionRemoveModal({ mapping, onClose, onRemoved }: BackupAttributionRemoveModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const directoryClientName = mapping.directory_client_name;
  const becomesInvisible = mapping.directory_clientID === null;

  const describeRevertTarget = (revertedTo: number | null): string => {
    if (revertedTo === null) {
      return 'no client — it is now invisible to every customer until its backup directory is mapped';
    }
    if (revertedTo === mapping.directory_clientID && directoryClientName !== null) {
      return directoryClientName;
    }
    return `client #${revertedTo}`;
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      const response = await apiFetch(`/admin/backup-vm-mappings/${mapping.id}`, { method: 'DELETE' });
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseBackupVmMappingDeleted | null;

      if (body?.status === 'success' && body.data?.deleted === true) {
        const revertedTo = body.data.reverted_to ?? null;
        addNotification({
          title: 'Attribution removed',
          srTitle: 'Success alert',
          description: `${mapping.vm_name} on ${mapping.hypervisor} reverted to ${describeRevertTarget(revertedTo)}. No backup data was deleted.`,
          variant: revertedTo === null ? 'warning' : 'success',
        });
        setDrawerOpen(true);
        onRemoved(mapping, revertedTo);
        onClose();
        return;
      }

      const message = body?.message ?? 'Could not remove the attribution. Please try again.';
      setError(message);
      addNotification({
        title: 'Could not remove the attribution',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      addNotification({
        title: 'Could not remove the attribution',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal variant="small" isOpen onClose={onClose}>
      <ModalHeader title="Remove attribution" titleIconVariant="warning" />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title="Remove failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        {becomesInvisible ? (
          <Alert variant="warning" isInline title="This VM will become invisible to every customer" className="pf-v6-u-mb-md">
            <strong>{mapping.vm_name}</strong>’s backup directory is not attributed to any client, so removing this
            attribution leaves the VM visible to no customer at all, until the directory is mapped or a new
            attribution is created.
          </Alert>
        ) : (
          <Content className="pf-v6-u-mb-md">
            <strong>{mapping.vm_name}</strong> will revert to{' '}
            <strong>{directoryClientName ?? `client #${mapping.directory_clientID}`}</strong>, the client its backup
            directory is mapped to.
          </Content>
        )}
        <Content>No backup data is deleted — only who can see it changes.</Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleDelete()} isLoading={isDeleting} isDisabled={isDeleting}>
          Remove attribution
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isDeleting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default BackupAttributionRemoveModal;
